import React, { useState } from 'react';

export default function KPISection({ kpis, metricsByYear }) {
    const [showInfo, setShowInfo] = useState(null);

    // Prepare historical data for trend charts
    const getHistoricalData = (metricKey) => {
        if (!metricsByYear) return [];

        const data = [];
        const sortedYears = Object.keys(metricsByYear).sort();
        const last10Years = sortedYears.slice(-10);

        for (const year of last10Years) {
            const value = metricsByYear[year][metricKey];
            if (value !== null && value !== undefined) {
                data.push({ year, value });
            }
        }

        return data;
    };

    // Simple line chart component
    const TrendChart = ({ data, unit, label }) => {
        if (!data || data.length === 0) return null;

        const values = data.map(d => d.value);
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        const range = maxValue - minValue;
        const padding = range * 0.1;

        const chartHeight = 200;
        const chartWidth = 500;
        const pointRadius = 4;

        // Calculate positions
        const points = data.map((d, i) => {
            const x = (i / (data.length - 1)) * chartWidth;
            const normalizedValue = (d.value - minValue + padding) / (range + 2 * padding);
            const y = chartHeight - (normalizedValue * chartHeight);
            return { x, y, value: d.value, year: d.year };
        });

        // Create SVG path
        const pathD = points.map((p, i) =>
            `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
        ).join(' ');

        const formatChartValue = (value) => {
            switch (unit) {
                case 'd': return `${value.toFixed(1)}d`;
                case '%': return `${value.toFixed(1)}%`;
                case 'x': return `${value.toFixed(2)}x`;
                case 'Cr': return `â‚¹${value.toFixed(0)} Cr`;
                default: return value.toFixed(2);
            }
        };

        return (
            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
                <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--brand-2)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                }}>
                    ðŸ“ˆ 10-Year Trend
                </h4>
                <div style={{
                    backgroundColor: 'var(--bg-3)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)'
                }}>
                    <svg
                        width="100%"
                        height={chartHeight + 40}
                        viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`}
                        style={{ overflow: 'visible' }}
                    >
                        {/* Grid lines */}
                        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                            const y = chartHeight - (ratio * chartHeight);
                            const value = minValue - padding + (ratio * (range + 2 * padding));
                            return (
                                <g key={i}>
                                    <line
                                        x1="0"
                                        y1={y}
                                        x2={chartWidth}
                                        y2={y}
                                        stroke="var(--border)"
                                        strokeWidth="1"
                                        strokeDasharray="3,3"
                                    />
                                    <text
                                        x="-5"
                                        y={y}
                                        textAnchor="end"
                                        alignmentBaseline="middle"
                                        fontSize="10"
                                        fill="var(--muted)"
                                    >
                                        {formatChartValue(value)}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Line */}
                        <path
                            d={pathD}
                            fill="none"
                            stroke="var(--brand-2)"
                            strokeWidth="2.5"
                        />

                        {/* Points */}
                        {points.map((p, i) => (
                            <g key={i}>
                                <circle
                                    cx={p.x}
                                    cy={p.y}
                                    r={pointRadius}
                                    fill="var(--card-bg)"
                                    stroke="var(--brand-2)"
                                    strokeWidth="2"
                                />
                                {/* Year labels */}
                                <text
                                    x={p.x}
                                    y={chartHeight + 15}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fill="var(--text-2)"
                                    fontWeight="500"
                                >
                                    {p.year}
                                </text>
                                {/* Value labels on hover */}
                                <title>{`${p.year}: ${formatChartValue(p.value)}`}</title>
                            </g>
                        ))}
                    </svg>
                </div>
            </div>
        );
    };

    if (!kpis || kpis.length === 0) return null;

    const formatValue = (value, unit) => {
        if (value === null || value === undefined) return 'N/A';

        switch (unit) {
            case 'd':
                return `${value.toFixed(2)}d`;
            case '%':
                return `${value.toFixed(2)}%`;
            case 'x':
                return `${value.toFixed(2)}x`;
            case 'Cr':
                return `â‚¹${value.toFixed(2)} Cr`;
            default:
                return value.toFixed(2);
        }
    };

    const getDeltaColor = (delta, better) => {
        if (delta === null || delta === undefined || delta === 0) return 'var(--muted)';

        let isImprovement = false;
        if (better === 'down') {
            isImprovement = delta < 0;
        } else if (better === 'up') {
            isImprovement = delta > 0;
        }

        return isImprovement ? 'var(--success)' : 'var(--danger)';
    };

    const formatDelta = (delta, unit) => {
        if (delta === null || delta === undefined) return 'N/A vs prev';

        const sign = delta >= 0 ? '+' : '';
        const absValue = Math.abs(delta);

        switch (unit) {
            case 'd':
                return `${sign}${absValue.toFixed(2)}d`;
            case '%':
                return `${sign}${absValue.toFixed(2)}%`;
            case 'x':
                return `${sign}${absValue.toFixed(2)}x`;
            case 'Cr':
                return `${sign}â‚¹${absValue.toFixed(2)} Cr`;
            default:
                return `${sign}${absValue.toFixed(2)}`;
        }
    };

    return (
        <>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                {kpis.map((kpi) => (
                    <div
                        key={kpi.key}
                        style={{
                            position: 'relative',
                            padding: '20px',
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '12px',
                            border: '1px solid var(--border)',
                            boxShadow: 'var(--shadow-sm)',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = 'var(--shadow)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {/* Info Icon */}
                        {kpi.info && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowInfo(showInfo === kpi.key ? null : kpi.key);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '12px',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    border: '1.5px solid var(--brand-2)',
                                    backgroundColor: showInfo === kpi.key ? 'var(--brand-2)' : 'transparent',
                                    color: showInfo === kpi.key ? '#ffffff' : 'var(--brand-2)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s',
                                    padding: 0
                                }}
                                onMouseEnter={(e) => {
                                    if (showInfo !== kpi.key) {
                                        e.currentTarget.style.backgroundColor = 'var(--surface-info)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (showInfo !== kpi.key) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                                title="Click for detailed information"
                            >
                                i
                            </button>
                        )}

                        <div style={{ fontSize: '14px', color: 'var(--text-2)', fontWeight: '600', marginBottom: '8px', paddingRight: '28px' }}>
                            {kpi.label}
                        </div>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--text-1)', marginBottom: '8px' }}>
                            {formatValue(kpi.value, kpi.unit)}
                        </div>
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                backgroundColor: getDeltaColor(kpi.delta, kpi.better) === 'var(--success)' ? 'var(--surface-success)' :
                                    getDeltaColor(kpi.delta, kpi.better) === 'var(--danger)' ? 'var(--surface-danger)' : 'var(--surface-neutral)',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: getDeltaColor(kpi.delta, kpi.better)
                            }}
                        >
                            {kpi.delta !== null && kpi.delta !== undefined && kpi.delta !== 0 && (
                                <span style={{ marginRight: '4px' }}>
                                    {kpi.delta > 0 ? 'â†‘' : 'â†“'}
                                </span>
                            )}
                            {formatDelta(kpi.delta, kpi.unit)} vs prev
                        </div>
                    </div>
                ))}
            </div>

            {/* Info Modal */}
            {showInfo && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'var(--overlay)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}
                    onClick={() => setShowInfo(null)}
                >
                    <div
                        style={{
                            backgroundColor: 'var(--card-bg)',
                            borderRadius: '16px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            boxShadow: 'var(--shadow)',
                            border: '1px solid var(--border)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {(() => {
                            const metric = kpis.find(k => k.key === showInfo);
                            if (!metric || !metric.info) return null;

                            return (
                                <>
                                    {/* Header */}
                                    <div style={{
                                        padding: '24px',
                                        borderBottom: '1px solid var(--border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <h3 style={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            color: 'var(--text-1)',
                                            margin: 0
                                        }}>
                                            {metric.label} - Detailed Information
                                        </h3>
                                        <button
                                            onClick={() => setShowInfo(null)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '24px',
                                                color: 'var(--muted)',
                                                cursor: 'pointer',
                                                padding: '0',
                                                width: '32px',
                                                height: '32px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '4px'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-3)'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            &times;
                                        </button>
                                    </div>

                                    {/* Content */}
                                    <div style={{ padding: '24px' }}>
                                        {/* Purpose */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: 'var(--brand-2)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '8px'
                                            }}>
                                                Purpose & Use
                                            </h4>
                                            <p style={{
                                                fontSize: '15px',
                                                color: 'var(--text-2)',
                                                lineHeight: '1.6',
                                                margin: 0
                                            }}>
                                                {metric.info.purpose}
                                            </p>
                                        </div>

                                        {/* Formula */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: 'var(--brand-2)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '8px'
                                            }}>
                                                Calculation Formula
                                            </h4>
                                            <div style={{
                                                backgroundColor: 'var(--bg-3)',
                                                padding: '12px 16px',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border)',
                                                fontFamily: 'monospace',
                                                fontSize: '14px',
                                                color: 'var(--text-1)'
                                            }}>
                                                {metric.info.formula}
                                            </div>
                                        </div>

                                        {/* Thresholds */}
                                        <div>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: 'var(--brand-2)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                marginBottom: '12px'
                                            }}>
                                                Performance Thresholds
                                            </h4>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {metric.info.thresholds.excellent && (
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        backgroundColor: 'var(--surface-success)',
                                                        borderLeft: '4px solid var(--success)',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--surface-success-text)', marginBottom: '2px' }}>
                                                            Excellent
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--surface-success-text)' }}>
                                                            {metric.info.thresholds.excellent}
                                                        </div>
                                                    </div>
                                                )}
                                                {metric.info.thresholds.good && (
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        backgroundColor: 'var(--surface-info)',
                                                        borderLeft: '4px solid var(--brand-2)',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--surface-info-text)', marginBottom: '2px' }}>
                                                            Good
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--surface-info-text)' }}>
                                                            {metric.info.thresholds.good}
                                                        </div>
                                                    </div>
                                                )}
                                                {metric.info.thresholds.moderate && (
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        backgroundColor: 'var(--surface-warning)',
                                                        borderLeft: '4px solid var(--warning)',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--surface-warning-text)', marginBottom: '2px' }}>
                                                            Moderate
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--surface-warning-text)' }}>
                                                            {metric.info.thresholds.moderate}
                                                        </div>
                                                    </div>
                                                )}
                                                {metric.info.thresholds.concern && (
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        backgroundColor: 'var(--surface-danger)',
                                                        borderLeft: '4px solid var(--danger)',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--surface-danger-text)', marginBottom: '2px' }}>
                                                            Concern
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--surface-danger-text)' }}>
                                                            {metric.info.thresholds.concern}
                                                        </div>
                                                    </div>
                                                )}
                                                {metric.info.thresholds.note && (
                                                    <div style={{
                                                        padding: '10px 14px',
                                                        backgroundColor: 'var(--surface-neutral)',
                                                        borderLeft: '4px solid var(--muted)',
                                                        borderRadius: '6px'
                                                    }}>
                                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-2)', marginBottom: '2px' }}>
                                                            Note
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>
                                                            {metric.info.thresholds.note}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Historical Trend Chart for specific metrics */}
                                        {showInfo === 'nwcPctSales' && metricsByYear && (
                                            <TrendChart
                                                data={getHistoricalData('nwcPctSales')}
                                                unit="%"
                                                label={metric.label}
                                            />
                                        )}
                                        {showInfo === 'tradeNWC' && metricsByYear && (
                                            <TrendChart
                                                data={getHistoricalData('tradeNWC')}
                                                unit="Cr"
                                                label={metric.label}
                                            />
                                        )}
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}
        </>
    );
}